/* One controller; no Swiper, duplicated cards or DOM-only partial search. */
(() => {
  const controllers = new Map();
  const BATCH = 24;
  const normalize = value => String(value).toLocaleLowerCase('de-DE')
    .replace(/ß/g, 'ss').replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue')
    .normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
    .replace(/ae/g, 'a').replace(/oe/g, 'o').replace(/ue/g, 'u')
    .replace(/[^\p{L}\p{N}]+/gu, ' ').trim();

  class RatgeberIndex {
    constructor(root) {
      this.root = root;
      if (root.dataset.catalogIncomplete === 'true') throw new Error('Incomplete native source catalog');
      this.grid = root.querySelector('[data-ratgeber-grid]');
      this.input = root.querySelector('[data-ratgeber-query]');
      this.more = root.querySelector('[data-ratgeber-more]');
      this.status = root.querySelector('[data-ratgeber-status]');
      this.empty = root.querySelector('[data-ratgeber-empty]');
      this.pagination = root.querySelector('[data-ratgeber-pagination]');
      this.topicOrder = Array.from(root.querySelectorAll('[data-topic-filter]'), button => button.dataset.topicFilter).filter(Boolean);
      this.total = Number(root.dataset.total);
      this.pageCount = Math.max(1, Number(root.dataset.pages));
      this.startPage = Number(root.dataset.page) || 1;
      this.serverRanked = root.dataset.initialOrder === 'ranked';
      this.serverSorted = this.serverRanked || root.dataset.initialOrder === 'server';
      this.displayOrder = null;
      try {
        const config = JSON.parse(root.querySelector('[data-ratgeber-order]')?.textContent || 'null');
        if (config && Array.isArray(config.ids) && Array.isArray(config.topics) &&
            config.ids.every(id => typeof id === 'string' && /^\d+$/.test(id)) &&
            new Set(config.ids).size === config.ids.length) {
          this.displayOrder = { topics: new Set(config.topics), ranks: new Map(config.ids.map((id, index) => [id, index])) };
        }
      } catch (_) { /* An invalid optional order keeps the existing catalog order. */ }
      this.cardNodes = new Map();
      this.initialCards = this.readCards(this.grid);
      this.initialPageCards = this.readCards(root.querySelector('[data-ratgeber-catalog]')?.content || this.grid);
      this.pages = new Map([[this.startPage, this.initialPageCards]]);
      this.requests = new Map();
      this.slots = [Promise.resolve(), Promise.resolve()];
      this.nextSlot = 0;
      this.abort = new AbortController();
      this.sequence = 0;
      this.limit = BATCH;
      this.topic = '';
      this.query = '';
      this.disposed = false;
      this.busy = false;
      this.stateKey = `ratgeber:${location.pathname}:${root.dataset.sectionId}:${new URLSearchParams(location.search).get('view') || ''}`;
      if (root.dataset.complete === 'true') this.validateComplete();
      const saved = history.state?.[this.stateKey];
      if (saved && saved.page === this.startPage) {
        this.query = String(saved.query || '').slice(0, 200);
        this.topic = String(saved.topic || '');
        this.limit = Math.max(BATCH, Number(saved.limit) || BATCH);
        if (!Array.from(root.querySelectorAll('[data-topic-filter]')).some(b => b.dataset.topicFilter === this.topic)) this.topic = '';
        this.input.value = this.query;
      }
      this.bind();
      root.querySelector('[data-ratgeber-topics]').hidden = false;
      // Keep SSR navigation until enhancement has successfully rendered a batch.
      root.classList.add('is-ready');
      this.update();
      if (this.serverSorted) this.scheduleCatalog();
    }

    readCards(scope) {
      return Array.from(scope.querySelectorAll('[data-ratgeber-card]'), node => {
        // Reuse the visible SSR node when the native catalog contains the same article.
        const existing = this.cardNodes.get(node.dataset.articleId);
        if (existing) return existing;
        const card = {
        id: node.dataset.articleId,
        topic: node.dataset.topic || '',
        text: normalize([
          node.querySelector('[data-card-title]')?.textContent,
          node.querySelector('[data-topic-label]')?.textContent,
          node.querySelector('[data-card-excerpt]')?.textContent,
          node.querySelector('[data-card-search-extra]')?.textContent,
        ].filter(Boolean).join(' ')),
        node,
        };
        this.cardNodes.set(card.id, card);
        return card;
      });
    }

    bind() {
      const options = { signal: this.abort.signal };
      this.input.addEventListener('input', event => {
        if (event.isComposing) return;
        this.criteriaChanged(true);
      }, options);
      this.input.addEventListener('compositionend', () => this.criteriaChanged(true), options);
      this.root.querySelector('[data-ratgeber-search]').addEventListener('submit', event => {
        event.preventDefault();
        this.criteriaChanged(true);
      }, options);
      this.root.addEventListener('click', event => {
        const target = event.target.closest('button');
        if (!target || !this.root.contains(target)) return;
        if (target.hasAttribute('data-topic-filter')) {
          this.topic = target.dataset.topicFilter;
          target.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
          this.criteriaChanged();
        } else if (target.hasAttribute('data-ratgeber-clear')) {
          this.input.value = '';
          this.input.focus();
          this.criteriaChanged();
        } else if (target.hasAttribute('data-ratgeber-reset')) {
          this.topic = '';
          this.input.value = '';
          this.input.focus();
          this.criteriaChanged();
        } else if (target.hasAttribute('data-ratgeber-more')) {
          if (this.busy) return;
          this.limit += BATCH;
          this.update();
        }
      }, options);
      window.addEventListener('pagehide', () => this.remember(), options);
      window.addEventListener('popstate', () => {
        const state = history.state?.[this.stateKey];
        this.input.value = state?.query || '';
        this.query = this.input.value;
        this.topic = state?.topic || '';
        this.limit = state?.limit || BATCH;
        this.update();
      }, options);
    }

    criteriaChanged(searchEdited = false) {
      // A new query searches all topics; a later tab click narrows those results.
      if (searchEdited && this.input.value !== this.query) this.topic = '';
      this.query = this.input.value;
      this.limit = BATCH;
      this.update();
    }

    orderedCards(from = 1) {
      const result = [];
      for (let page = from; page <= this.pageCount; page++) {
        if (!this.pages.has(page)) break;
        result.push(...this.pages.get(page));
      }
      return result;
    }

    mixOpening(cards) {
      const groups = this.topicOrder.map(topic => cards.filter(card => card.topic === topic)).filter(group => group.length);
      if (groups.length < 2) return cards;
      const opening = [];
      for (let round = 0; opening.length < BATCH; round++) {
        const next = groups.map(group => group[round]).filter(Boolean);
        if (!next.length) break;
        opening.push(...next.slice(0, BATCH - opening.length));
      }
      const selected = new Set(opening.map(card => card.id));
      // Only the first batch is mixed; the remainder keeps its original order.
      return [...opening, ...cards.filter(card => !selected.has(card.id))];
    }

    validateComplete() {
      const cards = this.orderedCards();
      if (this.pages.size !== this.pageCount || cards.length !== this.total || new Set(cards.map(c => c.id)).size !== this.total) {
        throw new Error('Incomplete or changed article catalog');
      }
      this.root.dataset.catalogReady = 'true';
    }

    scheduleCatalog() {
      if (this.pages.size === this.pageCount) return;
      const load = () => {
        if (this.disposed) return;
        // Warm only the inert search/filter catalog. Never render on completion.
        this.loadAll().catch(error => {
          if (/changed|inconsistent|Duplicate/.test(error.message)) this.resetCatalog();
        });
      };
      const idle = () => {
        this.backgroundTimer = setTimeout(() => {
          if ('requestIdleCallback' in window) this.backgroundIdle = requestIdleCallback(load, { timeout: 1500 });
          else load();
        }, 250);
      };
      // Let the document and its initial assets take priority over catalog pages.
      if (document.readyState === 'complete') idle();
      else window.addEventListener('load', idle, { once: true, signal: this.abort.signal });
    }

    resetCatalog() {
      this.pages = new Map([[this.startPage, this.initialPageCards]]);
      delete this.root.dataset.catalogReady;
    }

    async loadPage(number) {
      if (this.pages.has(number)) return;
      if (this.requests.has(number)) return this.requests.get(number);
      const slot = this.nextSlot++ % this.slots.length;
      const pending = this.slots[slot].catch(() => {}).then(async () => {
        if (this.disposed) throw new Error('Disposed section');
        const url = new URL(location.href);
        url.hash = '';
        url.searchParams.set(this.root.dataset.pageParam || 'page', number);
        url.searchParams.set('section_id', this.root.dataset.sectionId);
        // Preserve locale and the existing preview/view context; never follow external endpoints.
        const timeout = new AbortController();
        const timer = setTimeout(() => timeout.abort(), 12000);
        const stop = () => timeout.abort();
        this.abort.signal.addEventListener('abort', stop, { once: true });
        try {
          const response = await fetch(url, { signal: timeout.signal, credentials: 'same-origin', priority: 'low' });
          if (!response.ok) throw new Error('Section request failed');
          const html = await response.text();
          // Inert template parsing does not run returned scripts or attach off-screen images.
          const template = document.createElement('template');
          template.innerHTML = html;
          const section = template.content.querySelector('[data-ratgeber-index]');
          if (!section || section.dataset.sectionId !== this.root.dataset.sectionId || Number(section.dataset.total) !== this.total || Number(section.dataset.pages) !== this.pageCount || Number(section.dataset.page) !== number) {
            throw new Error('Missing or inconsistent section');
          }
          const cards = this.readCards(section.querySelector('[data-ratgeber-catalog]')?.content || section.querySelector('[data-ratgeber-grid]'));
          if (new Set(cards.map(c => c.id)).size !== cards.length) throw new Error('Duplicate article IDs');
          this.pages.set(number, cards);
        } finally {
          clearTimeout(timer);
          this.abort.signal.removeEventListener('abort', stop);
        }
      });
      this.slots[slot] = pending;
      this.requests.set(number, pending);
      try { await pending; } finally { this.requests.delete(number); }
    }

    async loadAll() {
      if (this.catalogRequest) return this.catalogRequest;
      const pending = this.fillCatalog();
      this.catalogRequest = pending;
      try { await pending; }
      finally { if (this.catalogRequest === pending) this.catalogRequest = null; }
    }

    async fillCatalog() {
      const missing = Array.from({ length: this.pageCount }, (_, i) => i + 1).filter(n => !this.pages.has(n));
      // At most two catalog requests in flight; all pages, not an arbitrary 50/250 cap.
      for (let i = 0; i < missing.length; i += 2) {
        const results = await Promise.allSettled(missing.slice(i, i + 2).map(n => this.loadPage(n)));
        const failure = results.find(r => r.status === 'rejected');
        if (failure) throw failure.reason;
      }
      this.validateComplete();
    }

    renderCards(cards) {
      const nodes = cards.map(card => card.node);
      const current = Array.from(this.grid.children);
      if (current.length === nodes.length && current.every((node, index) => node === nodes[index])) return;
      // Load-more appends only; the existing cards and their images stay attached.
      if (current.length < nodes.length && current.every((node, index) => node === nodes[index])) {
        this.grid.append(...nodes.slice(current.length));
      } else this.grid.replaceChildren(...nodes);
    }

    async update() {
      const sequence = ++this.sequence;
      const tokens = normalize(this.query).split(' ').filter(Boolean);
      const filtered = Boolean(tokens.length || this.topic);
      const ranked = this.displayOrder?.topics.has(this.topic) && (filtered || this.startPage === 1 || this.serverRanked);
      const serverOpening = this.serverRanked && !filtered && this.limit === BATCH;
      const mixedOpening = !ranked && !this.serverSorted && !filtered && this.startPage === 1 && this.topicOrder.length > 1;
      this.root.querySelectorAll('[data-topic-filter]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.topicFilter === this.topic)));
      this.root.querySelector('[data-ratgeber-clear]').hidden = !this.input.value;
      this.status.textContent = '';
      this.empty.hidden = true;
      this.busy = true;
      this.more.disabled = true;
      this.grid.setAttribute('aria-busy', 'true');
      try {
        if (serverOpening) {
          // The requested ranked page is already correct. Catalog requests must not
          // delay it, replace its nodes, or change the opening after a refresh.
          this.renderCards(this.initialCards);
          this.more.hidden = this.startPage * BATCH >= this.total;
          if (this.pagination) this.pagination.hidden = true;
          this.remember();
          return;
        }
        if (filtered || mixedOpening || ranked) {
          await this.loadAll();
        } else {
          const last = Math.min(this.pageCount, this.startPage + Math.ceil(this.limit / BATCH) - 1);
          for (let n = this.startPage; n <= last; n++) await this.loadPage(n);
        }
        if (this.disposed || sequence !== this.sequence) return;
        const source = this.orderedCards(filtered || ranked ? 1 : this.startPage);
        const matches = source.filter(card => (!this.topic || card.topic === this.topic) && tokens.every(token => card.text.includes(token)));
        if (new Set(matches.map(card => card.id)).size !== matches.length) throw new Error('Duplicate article catalog');
        const available = filtered ? matches.length : this.total - (this.startPage - 1) * BATCH;
        const ordered = ranked ? matches.slice().sort((a, b) =>
          (this.displayOrder.ranks.get(a.id) ?? Infinity) - (this.displayOrder.ranks.get(b.id) ?? Infinity)
        ) : mixedOpening ? this.mixOpening(matches) : matches;
        const offset = ranked && this.serverRanked && !filtered ? (this.startPage - 1) * BATCH : 0;
        const visible = ordered.slice(offset, offset + this.limit);
        // Reuse server-rendered nodes: one card instance, no HTML assembled from search input.
        this.renderCards(visible);
        this.empty.hidden = available !== 0;
        const hadMoreFocus = document.activeElement === this.more;
        this.more.hidden = visible.length >= available;
        this.status.textContent = '';
        if (hadMoreFocus && this.more.hidden) this.status.textContent = 'Alle Ratgeber dieser Auswahl sind geladen.';
        if (hadMoreFocus && this.more.hidden) this.status.focus({ preventScroll: true });
        // Preserve unfiltered SSR page links for discovery and the no-JavaScript fallback.
        if (this.pagination) {
          this.pagination.hidden = true;
          this.pagination.setAttribute('aria-label', 'Seiten des vollständigen Ratgeberbestands');
        }
        this.remember();
      } catch (error) {
        if (this.disposed || sequence !== this.sequence) return;
        // Enhancement failures must never replace readable SSR content with a notice.
        // Restore it even if the last successful search had no matches.
        if (!this.grid.querySelector('[data-ratgeber-card]')) {
          this.grid.replaceChildren(...this.initialCards.map(card => card.node));
        }
        if (/changed|inconsistent|Duplicate/.test(error.message)) {
          this.resetCatalog();
        }
        this.status.textContent = '';
        this.more.hidden = true;
        if (this.pagination) this.pagination.hidden = false;
      } finally {
        if (sequence === this.sequence && !this.disposed) {
          this.busy = false;
          this.more.disabled = false;
          this.grid.setAttribute('aria-busy', 'false');
        }
      }
    }

    remember() {
      try {
        history.replaceState({ ...history.state, [this.stateKey]: { page: this.startPage, query: this.query, topic: this.topic, limit: this.limit } }, '');
      } catch (_) { /* History storage must never prevent reading articles. */ }
    }

    destroy() {
      this.disposed = true;
      this.abort.abort();
      clearTimeout(this.backgroundTimer);
      if (this.backgroundIdle && 'cancelIdleCallback' in window) cancelIdleCallback(this.backgroundIdle);
      if (this.pagination) this.pagination.hidden = false;
      // Restore loaded SSR cards before an editor reinitialization.
      this.grid.replaceChildren(...this.initialCards.map(c => c.node));
    }
  }

  function initialize(scope = document) {
    scope.querySelectorAll('[data-ratgeber-index]').forEach(root => {
      if (controllers.has(root)) return;
      try { controllers.set(root, new RatgeberIndex(root)); }
      catch (_) { /* The unmodified server-rendered cards and links remain usable. */ }
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [root, controller] of controllers) {
      if (event.target.contains(root)) { controller.destroy(); controllers.delete(root); }
    }
  });
})();
